import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Box, Typography, styled } from '@mui/material';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSubscription } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import ChatSearchBar from './components/ChatSearchBar';
import NoChatsView from './components/NoChatsView';
import NoResultsView from './components/NoResultsView';
import Conversation from './Conversation/Conversation';
import ChatBox from './Messenger/ChatBox';
import {
  IChatList,
  ICoachMessagesPyalod,
  clearChatList,
  clearChatMessages,
  getChatList,
  setChatHistorySkip,
  setHasMoreChatHistory,
  setHasMoreMessages,
  setMessagesSkip,
} from '../../redux/chat/chatSlice';
import { useAppSelector } from '../../redux/hooks';
import SingleUser from './components/SingleUser';
import UserSearchBar from './components/UserSearchBar';
import { SUBSCRIBE_TO_COACH_MESSAGES } from '../../service/graphql/chatQuery';
import LoadingConversation from './Conversation/LoadingConversation';
import DefaultChatBox from './components/DefaultChatBox';
import { IClientNameListItem } from '../../types/client/IClientNameListItem';

const StyledGrid = styled(Grid)({
  display: 'flex',
  gap: '8px',
  padding: '16px',
  alignItems: 'center',
  borderBottom: '1px solid #FFDDC8',
  height: '60px',
});

function Chat() {
  const [searchText, setSearchText] = useState<string>('');
  const dispatch = useDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const [currentChat, setCurrentChat] = useState<null | IChatList>(null);
  const [currentUser, setCurrentUser] = useState<null | IClientNameListItem>(null);
  const {
    chatList,
    getChatListLoading,
    totalChatHistoryCount,
    sentMessage,
    skipChatHistory,
    hasMoreChatHistory,
  } = useAppSelector((state) => state.chat);
  const [showNewConversations, setShowNewConversations] = useState(false);
  const [conversations, setConversations] = useState<IChatList[]>([]);
  const [isChatHistoryExist, setIsChatHistoryExist] = useState<boolean>(false);

  useEffect(() => {
    if (!coachId) return;
    dispatch(getChatList({ coachId, skip: 0, take: 15 }));
  }, [coachId]);

  useEffect(() => {
    if (chatList) {
      setConversations(chatList);
    }
  }, [chatList]);

  useEffect(() => {
    if (totalChatHistoryCount > 0 && !isChatHistoryExist) {
      setIsChatHistoryExist(true);
    }
  }, [totalChatHistoryCount, isChatHistoryExist]);

  const { data } = useSubscription<ICoachMessagesPyalod>(SUBSCRIBE_TO_COACH_MESSAGES, {
    variables: { coachId },
    skip: !coachId,
  });

  useEffect(() => {
    if (data) {
      const conversation = conversations.find((c) => c.clientId === data.coachMessages.clientId);
      if (conversation) {
        const index = conversations.indexOf(conversation);
        const updatedConversations = [
          ...conversations.slice(0, index),
          ...conversations.slice(index + 1),
        ];
        updatedConversations.unshift({
          ...conversation,
          updatedAt: data.coachMessages.updatedAt,
          chatMessages: data.coachMessages.chatMessages,
        });
        setConversations(updatedConversations);
      } else {
        setConversations([data.coachMessages, ...conversations]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (sentMessage.message) {
      const conversation = conversations.find(
        (c) => c.chatMessages[0]?.chatId === sentMessage.message?.chatId
      );
      if (conversation) {
        const index = conversations.indexOf(conversation);
        const updatedConversations = [
          ...conversations.slice(0, index),
          ...conversations.slice(index + 1),
        ];
        updatedConversations.unshift({
          ...conversation,
          updatedAt: sentMessage.message.updatedAt,
          chatMessages: [sentMessage.message],
        });
        setConversations(updatedConversations);
      } else if (currentUser) {
        setConversations([
          {
            ...sentMessage.message,
            clientId: currentUser.id,
            chatMessages: [sentMessage.message],
            client: currentUser,
          },
          ...conversations,
        ]);
      }
    }
  }, [sentMessage]);

  const debouncedFetchChatHistory = useCallback(
    debounce(({ nameText }: { nameText: string }) => {
      if (!coachId) return;
      dispatch(
        getChatList({
          coachId,
          skip: 0,
          take: 15,
          filterValue: nameText,
          isFiltering: true,
        })
      );
    }, 1000),
    [coachId, dispatch]
  );

  useEffect(
    () => () => {
      debouncedFetchChatHistory.cancel();
      dispatch(clearChatList());
    },
    [debouncedFetchChatHistory]
  );

  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    debouncedFetchChatHistory({ nameText: event.target.value });
  };

  const handleClientClick = (client: IClientNameListItem) => {
    setShowNewConversations(false);
    const previousChatUser = conversations.find((chat) => chat.clientId === client.id);
    if (previousChatUser) {
      setCurrentChat(previousChatUser);
      setCurrentUser(null);
    } else {
      setCurrentUser(client);
      setCurrentChat(null);
    }
    dispatch(clearChatMessages());
  };

  const fetchMoreData = () => {
    if (chatList.length >= totalChatHistoryCount) {
      dispatch(setHasMoreChatHistory(false));
      return;
    }
    const newSkip = skipChatHistory + 15;
    dispatch(setChatHistorySkip(newSkip));
    dispatch(
      getChatList({
        coachId,
        skip: newSkip,
        take: 15,
        filterValue: searchText,
      })
    );
  };

  const handleConversationClick = (chat: IChatList) => {
    if (chat.clientId !== currentChat?.clientId) {
      setCurrentChat(chat);
      setCurrentUser(null);
      dispatch(clearChatMessages());
      setShowNewConversations(false);
      dispatch(setHasMoreMessages(true));
      dispatch(setMessagesSkip(0));
    }
  };

  useEffect(
    () => () => {
      dispatch(clearChatList());
      dispatch(clearChatMessages());
      dispatch(setHasMoreChatHistory(true));
      dispatch(setChatHistorySkip(0));
    },
    []
  );

  return (
    <Grid height="100%" container bgcolor="#fff">
      <Grid
        item
        xs={3}
        border="1px solid var(--Accent-Unbleached-Silk, #FFDDC8)"
        borderRadius="5px 0 0 5px"
        height="100%"
      >
        <ChatSearchBar
          searchText={searchText}
          searchHandler={searchHandler}
          onClick={() => {
            setShowNewConversations(true);
          }}
        />

        <Grid
          id="scrollableDiv"
          overflow="auto"
          height="calc(100vh - 160px)"
          sx={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#7CAEBE #fff', // thumb color track color
          }}
        >
          {getChatListLoading &&
            [...Array(5)].map((_count) => <LoadingConversation key={uuidv4()} />)}
          {chatList.length === 0 && !getChatListLoading ? (
            <NoResultsView />
          ) : (
            <InfiniteScroll
              dataLength={chatList.length}
              next={fetchMoreData}
              hasMore={hasMoreChatHistory}
              loader={<LoadingConversation />}
              scrollableTarget="scrollableDiv"
            >
              {conversations.map((chat) => (
                <Box
                  onClick={() => {
                    handleConversationClick(chat);
                  }}
                  key={chat.id}
                >
                  <Conversation chat={chat} isselectedChat={chat.id === currentChat?.id} />
                </Box>
              ))}
            </InfiniteScroll>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs={9}
        border="1px solid var(--Accent-Unbleached-Silk, #FFDDC8)"
        borderLeft="none"
        borderRadius="0 5px 5px 0"
      >
        {showNewConversations && !currentChat && !currentUser && (
          <Grid height="100%" display="flex" flexDirection="column">
            <StyledGrid>
              <UserSearchBar handleClientClick={handleClientClick} />
            </StyledGrid>
          </Grid>
        )}
        {currentChat && (
          <Grid height="100%" display="flex" flexDirection="column">
            <StyledGrid>
              {showNewConversations ? (
                <UserSearchBar handleClientClick={handleClientClick} />
              ) : (
                <>
                  <SingleUser
                    profilePic={currentChat.client.profilePic ?? ''}
                    firstName={currentChat.client.firstName}
                    lastName={currentChat.client.lastName ?? ''}
                    hideStatus
                  />
                  <Grid gap={1} display="flex" flexDirection="row">
                    <Typography variant="bodyBold" color="primary">
                      {currentChat.client.firstName} {currentChat.client.lastName}
                    </Typography>
                    {currentChat.client.archived && (
                      <img src="/assets/icons/archive.svg" alt="archive" />
                    )}
                  </Grid>
                </>
              )}
            </StyledGrid>
            <ChatBox client={currentChat.client} data={data as ICoachMessagesPyalod} />
          </Grid>
        )}
        {currentUser && (
          <Grid height="100%" display="flex" flexDirection="column">
            <StyledGrid>
              {showNewConversations ? (
                <UserSearchBar handleClientClick={handleClientClick} />
              ) : (
                <>
                  <SingleUser
                    profilePic={currentUser.profilePic ?? ''}
                    firstName={currentUser.firstName}
                    lastName={currentUser.lastName ?? ''}
                    hideStatus
                  />
                  <Typography variant="bodyBold" color="primary">
                    {currentUser.firstName} {currentUser.lastName}
                  </Typography>
                </>
              )}
            </StyledGrid>
            <ChatBox client={currentUser} data={data as ICoachMessagesPyalod} />
          </Grid>
        )}
        {chatList.length === 0 &&
          !getChatListLoading &&
          !showNewConversations &&
          !isChatHistoryExist &&
          !currentUser && <NoChatsView />}
        {!currentChat && !showNewConversations && !currentUser && isChatHistoryExist && (
          <DefaultChatBox />
        )}
      </Grid>
    </Grid>
  );
}

export default Chat;
